import { formatList } from "@iec-id/shared";
import type { SessionHint } from "@iec-id/shared/contracts/auth";
import { AvatarIcon, Button, Hint, initialsOf, useOnline } from "@iec-id/ui";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	type AuthSearch,
	fallbackDestination,
	firstNameOf,
	needsAccountChooser,
	requestSilentAuthorization,
	resolveService,
} from "@/lib/auth-flow";
import { useLocale } from "@/lib/locale";
import { AuthCard, AuthHeading, OfflineNotice } from "./auth-card";
import { ServiceBadge } from "./service-badge";
import { useSigninT } from "./signin-i18n";

type ActiveHint = Extract<SessionHint, { state: "session" }>;

/**
 * Account chooser (макет 4a, карточка 3).
 *
 * Экран существует ради одного обещания: сервис не узнаёт о человеке ничего,
 * пока тот не нажал «Продолжить». Поэтому здесь нет ни полной почты, ни `sub`
 * (инвариант №5) — только маска, имя и список того, что уйдёт после согласия.
 * Локальный аккаунт в сервисе создаётся тоже после, а не заранее.
 */
export function ContinueStep({ search, hint }: { search: AuthSearch; hint: ActiveHint }) {
	const t = useSigninT();
	const { t: tCommon } = useTranslation("common");
	const locale = useLocale();
	const online = useOnline();
	const navigate = useNavigate();
	const service = resolveService(search);

	const [handingOff, setHandingOff] = useState(false);
	const [failure, setFailure] = useState<string | null>(null);

	/**
	 * Продолжение — это обмен живой сессии на код авторизации через
	 * `/api/auth/check`. Не ответил (или ответил без адреса) — уводим на витрину
	 * или на сайт сервиса: сессия-то есть, тупик человеку не нужен.
	 */
	const handleContinue = async () => {
		setFailure(null);
		setHandingOff(true);
		if (!needsAccountChooser(search)) {
			window.location.assign(fallbackDestination(search));
			return;
		}
		try {
			const silent = await requestSilentAuthorization(search);
			window.location.assign(silent.redirectUrl ?? fallbackDestination(search));
		} catch {
			setFailure(t("continue.failed"));
			setHandingOff(false);
		}
	};

	// Названия полей — ключи, а не готовые строки: сервер присылает `avatar`,
	// а человек должен прочитать «фото». Незнакомое поле показываем как есть,
	// молча выкидывать из списка нельзя — это обещание о передаче данных.
	const fields = formatList(
		hint.sharedFields.map((field) => {
			const key = `continue.field.${field}`;
			const label = t(key);
			return label === key ? field : label;
		}),
		locale,
	);

	return (
		<AuthCard>
			<AuthHeading
				title={
					service ? t("continue.title", { service: service.name }) : t("continue.titleGeneric")
				}
				subtitle={t("continue.subtitle")}
				{...(service ? { badge: <ServiceBadge service={service} /> } : {})}
			/>

			<OfflineNotice />

			<div className="flex items-center gap-3.5 rounded-control bg-sunken p-4">
				<AvatarIcon
					size={48}
					initials={initialsOf(hint.displayName)}
					{...(hint.avatarUrl ? { src: hint.avatarUrl } : {})}
					alt={hint.displayName}
				/>
				<div className="flex min-w-0 flex-col gap-0.5">
					<span className="t-row truncate text-fg">{hint.displayName}</span>
					{/* Маска, а не адрес: полная почта до входа не показывается никогда. */}
					<span className="t-meta truncate text-fg-muted">{hint.maskedEmail}</span>
				</div>
			</div>

			<div className="flex items-start gap-2">
				<p className="t-meta text-fg-muted">
					{service
						? t("continue.shares", { service: service.name, fields })
						: t("continue.sharesGeneric", { fields })}
				</p>
				<Hint label={t("continue.tooltip")} triggerLabel={tCommon("action.hint")} />
			</div>

			{failure ? (
				<p role="alert" className="t-body text-danger">
					{failure}
				</p>
			) : null}

			<div className="flex flex-col gap-2.5">
				<Button
					variant="primary"
					fullWidth
					state={handingOff ? "loading" : online ? "default" : "disabled"}
					onClick={() => void handleContinue()}
				>
					{t("continue.continueAs", { name: firstNameOf(hint.displayName) })}
				</Button>
				<Button
					variant="second"
					fullWidth
					state={handingOff ? "disabled" : "default"}
					onClick={() => {
						// `login=true` — единственный способ заставить форму показаться
						// поверх живой сессии; без него `/` вернёт человека сюда же.
						void navigate({ to: "/", search: { ...search, login: "true" }, replace: true });
					}}
				>
					{t("continue.another")}
				</Button>
			</div>
		</AuthCard>
	);
}
